// Operator-API-Client — getrennt vom Sachbearbeiter-`client.ts`. Eigener
// Refresh-on-401 (gegen /api/operator/refresh), damit eine abgelaufene
// Operator-Session nicht den Sachbearbeiter-Refresh zieht.

import {
  clearOperatorSession,
  getOperatorSession,
  setOperatorSession,
  type OperatorSession,
} from "./session";

const BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

export class OperatorApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export interface FirmSummary {
  id: string;
  name: string;
  slug: string;
  plan: string;
  active: boolean;
  seats_licensed: number;
  user_count: number;
  installation_count: number;
  created_at: string;
}

export interface FirmUser {
  id: string;
  email: string;
  full_name: string;
  active: boolean;
}

export interface FirmDetail extends FirmSummary {
  strasse: string | null;
  plz: string | null;
  ort: string | null;
  telefon: string | null;
  kontakt_email: string | null;
  mandanten_count: number;
  active_workflow_count: number;
  device_client_count: number;
  web_invite_count: number;
  users: FirmUser[];
}

export interface MandantItem {
  id: string;
  bmd_kundennr: string;
  name: string;
  has_device_client: boolean;
  web_invite_count: number;
}

export const getFirmMandanten = (firmId: string): Promise<MandantItem[]> =>
  authedGet<MandantItem[]>(`/api/operator/firms/${firmId}/mandanten`);

async function refreshAccessToken(): Promise<string | null> {
  const session = getOperatorSession();
  if (!session) return null;
  const res = await fetch(`${BASE_URL}/api/operator/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ refresh_token: session.refresh_token }),
  });
  if (!res.ok) {
    clearOperatorSession();
    return null;
  }
  const data = await res.json();
  const next: OperatorSession = {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
  };
  setOperatorSession(next);
  return next.access_token;
}

async function authedGet<T>(path: string): Promise<T> {
  const session = getOperatorSession();
  if (!session) throw new OperatorApiError("Nicht angemeldet", 401);

  const call = (token: string) =>
    fetch(`${BASE_URL}${path}`, {
      headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    });

  let res = await call(session.access_token);
  if (res.status === 401) {
    const fresh = await refreshAccessToken();
    if (!fresh) throw new OperatorApiError("Sitzung abgelaufen", 401);
    res = await call(fresh);
  }
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      if (body?.detail) detail = String(body.detail);
    } catch {
      /* kein JSON-Body */
    }
    throw new OperatorApiError(detail, res.status);
  }
  return (await res.json()) as T;
}

export async function login(
  email: string,
  password: string
): Promise<OperatorSession> {
  const res = await fetch(`${BASE_URL}/api/operator/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    throw new OperatorApiError("E-Mail oder Passwort falsch", res.status);
  }
  const data = await res.json();
  return {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
  };
}

export const getFirms = (): Promise<FirmSummary[]> =>
  authedGet<FirmSummary[]>("/api/operator/firms");

export const getFirm = (id: string): Promise<FirmDetail> =>
  authedGet<FirmDetail>(`/api/operator/firms/${id}`);

// --- Provisioning (Phase 2) ---------------------------------------------

async function authedPost<T>(path: string, body: unknown): Promise<T> {
  const session = getOperatorSession();
  if (!session) throw new OperatorApiError("Nicht angemeldet", 401);

  const call = (token: string) =>
    fetch(`${BASE_URL}${path}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });

  let res = await call(session.access_token);
  if (res.status === 401) {
    const fresh = await refreshAccessToken();
    if (!fresh) throw new OperatorApiError("Sitzung abgelaufen", 401);
    res = await call(fresh);
  }
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body2 = await res.json();
      if (body2?.detail) detail = String(body2.detail);
    } catch {
      /* kein JSON-Body */
    }
    throw new OperatorApiError(detail, res.status);
  }
  return (await res.json()) as T;
}

export interface CreateFirmInput {
  name: string;
  slug: string;
  plan: string;
  strasse?: string;
  plz?: string;
  ort?: string;
  telefon?: string;
  kontakt_email?: string;
}
export interface CreateFirmResult {
  firm: FirmSummary;
  bot_key: string;
  key_label: string;
}
export const createFirm = (input: CreateFirmInput): Promise<CreateFirmResult> =>
  authedPost<CreateFirmResult>("/api/operator/firms", input);

export interface CreateUserInput {
  email: string;
  full_name: string;
  password?: string;
}
export interface CreateUserResult {
  id: string;
  email: string;
  full_name: string;
  generated_password: string | null;
}
export const createFirmUser = (
  firmId: string,
  input: CreateUserInput
): Promise<CreateUserResult> =>
  authedPost<CreateUserResult>(`/api/operator/firms/${firmId}/users`, input);

export interface CreateBotKeyResult {
  key_id: string;
  label: string;
  bot_key: string;
}
export const createBotKey = (
  firmId: string,
  label: string
): Promise<CreateBotKeyResult> =>
  authedPost<CreateBotKeyResult>(`/api/operator/firms/${firmId}/bot-key`, {
    label,
  });

// --- Workflows (Phase 3) ------------------------------------------------

export interface OpWorkflow {
  workflow_id: string;
  trigger: string;
  active_version: number | null;
  latest_version: number;
  step_count: number;
  steps_plain: string[];
  version_count: number;
}

export interface WorkflowState {
  workflow_id: string;
  version: number;
  active: boolean;
}

export const getFirmWorkflows = (firmId: string): Promise<OpWorkflow[]> =>
  authedGet<OpWorkflow[]>(`/api/operator/firms/${firmId}/workflows`);

export const importWorkflow = (
  firmId: string,
  definition: unknown
): Promise<WorkflowState> =>
  authedPost<WorkflowState>(`/api/operator/firms/${firmId}/workflows`, definition);

export const activateWorkflow = (
  firmId: string,
  workflowId: string,
  version: number
): Promise<WorkflowState> =>
  authedPost<WorkflowState>(
    `/api/operator/firms/${firmId}/workflows/${encodeURIComponent(
      workflowId
    )}/activate?version=${version}`,
    {}
  );

export const deactivateWorkflow = (
  firmId: string,
  workflowId: string
): Promise<WorkflowState> =>
  authedPost<WorkflowState>(
    `/api/operator/firms/${firmId}/workflows/${encodeURIComponent(
      workflowId
    )}/deactivate`,
    {}
  );

// --- Audit-Protokoll (Phase 4) ------------------------------------------

export interface AuditEntry {
  id: string;
  operator_email: string;
  action: string;
  firm_slug: string | null;
  detail: Record<string, unknown>;
  created_at: string;
}

export const getAudit = (): Promise<AuditEntry[]> =>
  authedGet<AuditEntry[]>("/api/operator/audit");
